import React from 'react';
import { calculateSKS, calculateOtpadObsh, calculateOtpadTek, calculateRaschetnayaOstatochnayaPolnota, calculateKlassUstoychivostiNasazhdeniya } from './utils';
import { calculateDamagePercentage as calculateForm5DamagePercentage } from '../Form5/utils';
import DecimalInput from '../../fields/DecimalInput';
import { isForestCoveredLandCategory } from '../Form2/utils';

const DataSection = ({
  displayData,
  updateCommonField,
  independentForm2Data,
  calculateTotalsByNasazhdeniya,
  tableScrollRef,
  tableScrollPosition,
  handleTableSliderChange,
  handleTableScroll,
  hasForm4Data = false,
  form4SavedModalData = {},
  form5Data = null,
  selectedForm45 = 4,
  handbooks = [],
  landCategory = ''
}) => {
  const getForm5DamageForRow = (item, rowIndex) => {
    if (!form5Data || !Array.isArray(form5Data.data)) {
      return '0.00';
    }

    const dataArray = form5Data.data;
    let matchedRow = null;

    // 1) Самый точный вариант: совпадение по id строки
    if (item?.id !== undefined && item?.id !== null) {
      matchedRow = dataArray.find(f5Item => String(f5Item.id) === String(item.id));
    }

    // 2) Резерв: совпадение по позиции строки
    if (!matchedRow && typeof rowIndex === 'number' && rowIndex >= 0 && rowIndex < dataArray.length) {
      matchedRow = dataArray[rowIndex];
    }

    // 3) Доп. резерв: ярус + порода + доля (чтобы не путать дубликаты по ярусу/породе)
    if (!matchedRow) {
      matchedRow = dataArray.find(f5Item =>
        (String(f5Item.yarus) === String(item.yarus)) &&
        (String(f5Item.kodporoda) === String(item.poroda)) &&
        (String(f5Item.dolap ?? '') === String(item.dolya ?? ''))
      );
    }

    // 4) Последний резерв: старое сопоставление ярус + порода
    if (!matchedRow) {
      matchedRow = dataArray.find(f5Item =>
        (String(f5Item.yarus) === String(item.yarus)) &&
        (String(f5Item.kodporoda) === String(item.poroda))
      );
    }

    if (!matchedRow) {
      return '0.00';
    }

    const damage = calculateForm5DamagePercentage(matchedRow);
    return String(damage);
  };
  
  // Функция для форматирования данных о причинах повреждения для строки
  const formatCausesForRow = (rowId, useSpecificDola = false) => {
    const modalDataForRow = form4SavedModalData[rowId];
    if (!modalDataForRow || !Array.isArray(modalDataForRow) || modalDataForRow.length === 0) {
      return '';
    }
    
    // Получаем справочник причин (ID 10) для получения code
    const causesHandbook = handbooks.find(h => h.id === 10);
    
    // Собираем все причины с признаками и долями
    const causesList = [];
    
    modalDataForRow.forEach((modalItem) => {
      if (!modalItem.prichina) {
        return; // Пропускаем записи без причины
      }
      
      // Получаем code причины из справочника
      let causeCode = modalItem.prichina; // По умолчанию используем ID, если не найдём code
      if (causesHandbook && causesHandbook.references) {
        const cause = causesHandbook.references.find(
          r => r.id.toString() === String(modalItem.prichina)
        );
        if (cause && cause.code) {
          causeCode = cause.code.toString();
        }
      }
      
      // Выбираем долю в зависимости от параметра
      let dola = '';
      if (useSpecificDola) {
        // Для определения очага используем специальную долю, если её нет - 0
        dola = modalItem.specificDolaPrizn || '0';
      } else {
        // Для обычного столбца используем обычную долю
        if (!modalItem.dolaprizn) {
          return; // Пропускаем записи без доли
        }
        dola = modalItem.dolaprizn;
      }
      
      // Получаем code признаков
      let priznCodes = [];
      
      // Получаем справочник признаков (ID 9 - DamageCodes)
      const signsHandbook = handbooks.find(h => h.id === 9);
      
      // Если prizn - это строка (ID), пытаемся найти code
      if (modalItem.prizn) {
        if (signsHandbook && signsHandbook.references) {
          const sign = signsHandbook.references.find(
            r => r.id.toString() === String(modalItem.prizn)
          );
          if (sign && sign.code) {
            priznCodes.push(sign.code.toString());
          } else {
            // Если не нашли code, используем ID
            priznCodes.push(modalItem.prizn);
          }
        } else {
          priznCodes.push(modalItem.prizn);
        }
      }
      
      // Также проверяем, есть ли priznList (массив признаков)
      if (modalItem.priznList && Array.isArray(modalItem.priznList) && modalItem.priznList.length > 0) {
        priznCodes = modalItem.priznList.map(priznItem => {
          if (typeof priznItem === 'string') {
            // Если это строка (ID), пытаемся найти code
            if (signsHandbook && signsHandbook.references) {
              const sign = signsHandbook.references.find(
                r => r.id.toString() === String(priznItem)
              );
              if (sign && sign.code) {
                return sign.code.toString();
              }
            }
            return priznItem;
          }
          if (priznItem && priznItem.code) {
            return priznItem.code.toString();
          }
          if (priznItem && priznItem.id) {
            // Если есть только id, пытаемся найти code
            if (signsHandbook && signsHandbook.references) {
              const sign = signsHandbook.references.find(
                r => r.id.toString() === String(priznItem.id)
              );
              if (sign && sign.code) {
                return sign.code.toString();
              }
            }
            return priznItem.id.toString();
          }
          return '';
        }).filter(code => code); // Убираем пустые строки
      }
      
      // Форматируем: "code_причины(code_признаков)/доля"
      const priznStr = priznCodes.length > 0 ? `(${priznCodes.join(', ')})` : '';
      const formattedCause = `${causeCode}${priznStr}/${dola}`;
      causesList.push(formattedCause);
    });
    
    // Объединяем все причины через ;
    return causesList.join('; ');
  };
  // Получаем итог по насаждению для расчета
  const totals = calculateTotalsByNasazhdeniya ? calculateTotalsByNasazhdeniya() : {};
  const otpadObshTotal = totals.otpad_obsh || '0.00';
  const sksTotal = totals.sksRaw ?? totals.sks;
  
  // Рассчитываем расчётную остаточную полноту
  const raschetnayaOstatochnayaPolnota = calculateRaschetnayaOstatochnayaPolnota(independentForm2Data, totals.otpad_obshRaw ?? otpadObshTotal);
  
  // Рассчитываем класс устойчивости насаждения
  const hasPlantation = isForestCoveredLandCategory(landCategory) && displayData.data.some(item =>
    String(item.yarus ?? '').trim() !== '' &&
    String(item.poroda ?? '').trim() !== '' &&
    (item.dolya === '+' || Number(item.dolya) > 0)
  );
  const klassUstoychivostiNasazhdeniya = hasPlantation
    ? calculateKlassUstoychivostiNasazhdeniya(sksTotal)
    : '';
  return (
    <div className="group-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h4>Сводка по состоянию насаждения</h4>
      </div>
      
      <div 
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        style={{ 
          overflowX: 'auto', 
          overflowY: 'visible',
          maxWidth: '100%',
          border: '1px solid #ddd',
          borderRadius: '6px'
        }}>
        <table className="table" style={{ 
          minWidth: (selectedForm45 === 4 && hasForm4Data) ? '1440px' : (selectedForm45 === 5 ? '800px' : (selectedForm45 === 4 ? '1040px' : '600px')),
          width: '100%',
          marginBottom: 0
        }}>
          <thead>
            <tr>
              <th style={{ minWidth: '80px', width: '80px' }}>Ярус</th>
              <th style={{ minWidth: '100px', width: '100px' }}>Доля</th>
              <th style={{ minWidth: '150px', width: '150px' }}>Порода</th>
              {selectedForm45 === 4 && (
                <>
                  <th style={{ minWidth: '100px', width: '100px' }}>СКС</th>
                  <th style={{ minWidth: '120px', width: '120px' }}>Отпад общий</th>
                  <th style={{ minWidth: '120px', width: '120px' }}>Отпад текущий</th>
                </>
              )}
              {(selectedForm45 === 4 && hasForm4Data) || selectedForm45 === 5 ? (
                <th style={{ minWidth: '200px', width: '200px' }}>
                  {selectedForm45 === 5
                    ? 'Повреждение крон'
                    : 'Доля повреждённых деревьев по причинам повреждения, % от запаса'}
                </th>
              ) : null}
              {selectedForm45 === 4 && hasForm4Data && (
                <th style={{ minWidth: '200px', width: '200px' }}>Доля повреждённых деревьев по причинам повреждения для определения очага, % от запаса</th>
              )}
              {selectedForm45 === 4 && (
                <>
                  <th style={{ minWidth: '180px', width: '180px' }}>Расчётная остаточная полнота</th>
                  <th style={{ minWidth: '180px', width: '180px' }}>Класс устойчивости насаждения</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {displayData.data.map((item, index) => (
              <tr key={item.id}>
                {/* Ярус */}
                <td className="coordinate-cell" style={{ minWidth: '80px', width: '80px' }}>
                  <input
                    type="text"
                    value={item.yarus}
                    readOnly
                    className="form-control coordinate-field"
                    style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>

                {/* Доля */}
                <td className="coordinate-cell" style={{ minWidth: '100px', width: '100px' }}>
                  <input
                    type="text"
                    value={item.dolya}
                    readOnly
                    className="form-control coordinate-field"
                    style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>

                {/* Порода */}
                <td className="coordinate-cell" style={{ minWidth: '150px', width: '150px' }}>
                  <input
                    type="text"
                    value={(() => {
                      const handbook = handbooks?.find(h => h.id === 31);
                      if (handbook && handbook.references && item.poroda) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === String(item.poroda));
                        return selectedOption ? selectedOption.name : item.poroda;
                      }
                      return item.poroda || '';
                    })()}
                    readOnly
                    className="form-control coordinate-field"
                    style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>
                
                {/* СКС - только для формы 4 */}
                {selectedForm45 === 4 && (
                  <td className="coordinate-cell" style={{ minWidth: '100px', width: '100px' }}>
                    <DecimalInput
                      value={calculateSKS(item)}
                      readOnly
                      className="form-control coordinate-field"
                      style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </td>
                )}
                
                {/* Отпад общий - только для формы 4 */}
                {selectedForm45 === 4 && (
                  <td className="coordinate-cell" style={{ minWidth: '120px', width: '120px' }}>
                    <DecimalInput
                      value={calculateOtpadObsh(item)}
                      readOnly
                      className="form-control coordinate-field"
                      style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </td>
                )}
                
                {/* Отпад текущий - только для формы 4 */}
                {selectedForm45 === 4 && (
                  <td className="coordinate-cell" style={{ minWidth: '120px', width: '120px' }}>
                    <DecimalInput
                      value={calculateOtpadTek(item)}
                      readOnly
                      className="form-control coordinate-field"
                      style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </td>
                )}
                
                {/* Доля повреждённых деревьев по причинам повреждения, % от запаса */}
                {selectedForm45 === 4 && hasForm4Data && (
                  <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px', textAlign: 'center' }}>
                    <div
                      style={{
                        padding: '8px',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: '40px',
                        background: '#f8f9fa',
                        textAlign: 'center'
                      }}
                    >
                      {formatCausesForRow(item.id)}
                    </div>
                  </td>
                )}
                {selectedForm45 === 5 && form5Data && form5Data.data && (
                  <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px', textAlign: 'center' }}>
                    <div
                      style={{
                        padding: '8px',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: '40px',
                        background: '#f8f9fa',
                        textAlign: 'center'
                      }}
                    >
                      {getForm5DamageForRow(item, index)}
                    </div>
                  </td>
                )}
                
                {/* Доля повреждённых деревьев по причинам повреждения для определения очага, % от запаса - только если заполнена форма 4 */}
                {selectedForm45 === 4 && hasForm4Data && (
                  <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px', textAlign: 'center' }}>
                    <div
                      style={{
                        padding: '8px',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minHeight: '40px',
                        background: '#f8f9fa',
                        textAlign: 'center'
                      }}
                    >
                      {formatCausesForRow(item.id, true)}
                    </div>
                  </td>
                )}
                
                {/* Расчётная остаточная полнота - объединённая ячейка (расчётное поле) - только для формы 4 */}
                {selectedForm45 === 4 && index === 0 && (
                  <td 
                    className="coordinate-cell" 
                    rowSpan={displayData.data.length}
                    style={{ minWidth: '180px', width: '180px', verticalAlign: 'top' }}
                  >
                    <DecimalInput
                      value={raschetnayaOstatochnayaPolnota.toFixed(2)}
                      readOnly
                      className="form-control coordinate-field"
                      style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </td>
                )}
                
                {/* Класс устойчивости насаждения - объединённая ячейка (расчётное поле) - только для формы 4 */}
                {selectedForm45 === 4 && index === 0 && (
                  <td 
                    className="coordinate-cell" 
                    rowSpan={displayData.data.length}
                    style={{ minWidth: '180px', width: '180px', verticalAlign: 'top' }}
                  >
                    <input
                      type="text"
                      value={klassUstoychivostiNasazhdeniya}
                      aria-label="Класс устойчивости насаждения"
                      readOnly
                      className="form-control coordinate-field"
                      style={{ background: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Слайдбар для прокрутки таблицы */}
      <div style={{
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '60px'
        }}>
          Прокрутка:
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={tableScrollPosition}
          onChange={handleTableSliderChange}
          className="table-scroll-slider"
          style={{
            flex: 1
          }}
        />
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '30px',
          textAlign: 'right'
        }}>
          {Math.round(tableScrollPosition)}%
        </span>
      </div>
    </div>
  );
};

export default DataSection;

